import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Power } from 'lucide-react';
import { activityInputSchema, type ActivityInput } from '@timesheet/shared';
import { useCrud } from '@/hooks/useCrud';
import { Modal } from '@/components/ui/Modal';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { CrudHeader } from '@/components/crud/CrudHeader';

interface ActivityRow {
  id: string;
  code: string;
  name: string;
  chargeable: boolean;
  active: boolean;
}

export function ActivitiesPage() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ActivityRow | null>(null);
  const [creating, setCreating] = useState(false);

  const crud = useCrud<ActivityRow, ActivityInput>({
    basePath: '/admin/activities',
    queryKey: ['activities'],
    listParams: { active: 'all', q: search || undefined },
  });

  const columns: Column<ActivityRow>[] = [
    { key: 'code', header: 'Code', className: 'font-mono font-medium' },
    { key: 'name', header: 'Name', grow: true },
    {
      key: 'chargeable',
      header: 'Type',
      render: (r) => (
        <Badge tone={r.chargeable ? 'info' : 'neutral'}>
          {r.chargeable ? 'Chargeable' : 'Non-chargeable'}
        </Badge>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.active ? 'success' : 'neutral'}>
          {r.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <CrudHeader
        title="Activities"
        description="Time-coding categories (e.g. fieldwork, review, partner sign-off)."
        searchValue={search}
        onSearchChange={setSearch}
        onCreate={() => setCreating(true)}
        createLabel="New activity"
      />

      <DataTable
        columns={columns}
        rows={crud.list.data?.items}
        loading={crud.list.isPending}
        total={crud.list.data?.total}
        rowKey={(r) => r.id}
        actions={(r) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </Button>
            {r.active && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Deactivate activity "${r.code} — ${r.name}"?`)) {
                    crud.remove.mutate(r.id);
                  }
                }}
              >
                <Power className="h-3.5 w-3.5 text-brand-danger" aria-hidden />
              </Button>
            )}
          </div>
        )}
      />

      <ActivityFormModal
        key={creating ? 'new' : editing?.id ?? 'closed'}
        open={creating || !!editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={async (input) => {
          if (editing) {
            await crud.update.mutateAsync({ id: editing.id, input });
            setEditing(null);
          } else {
            await crud.create.mutateAsync(input);
            setCreating(false);
          }
        }}
        title={editing ? `Edit activity — ${editing.code}` : 'New activity'}
        defaultValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                chargeable: editing.chargeable,
                active: editing.active,
              }
            : undefined
        }
      />
    </div>
  );
}

interface ActivityFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: ActivityInput) => Promise<void>;
  title: string;
  defaultValues?: Partial<ActivityInput>;
}

function ActivityFormModal({ open, onClose, onSubmit, title, defaultValues }: ActivityFormModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ActivityInput>({
    resolver: zodResolver(activityInputSchema),
    defaultValues: {
      code: '',
      name: '',
      chargeable: true,
      active: true,
      ...defaultValues,
    },
  });

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (e) {
      alert((e as Error).message);
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting} type="button">
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Code" error={errors.code?.message} required>
          {(p) => <Input {...p} {...register('code')} placeholder="AUDIT_FW" />}
        </Field>
        <Field label="Name" error={errors.name?.message} required>
          {(p) => <Input {...p} {...register('name')} placeholder="Audit fieldwork" />}
        </Field>
        <Checkbox label="Chargeable to client" {...register('chargeable')} defaultChecked />
        <Checkbox label="Active" {...register('active')} defaultChecked />
      </form>
    </Modal>
  );
}
